/* ===========================================================================
   gnpab-prodrow.js — auto-advance for the SHARED featured-collection product
   row. Pairs with snippets/gnpab-prodrow.liquid, which loads it.

   MOVED OUT OF gnpab-motion.js ON 2026-09-10 so the blog index drives the same
   row with the same code instead of a second copy. The logic below is the
   About page's, unchanged except for two things, both named here because a
   silent change inside a lift is how a signed-off page regresses:

     1. the row is found by GNPAB_PRODROW_SCOPE (set by the snippet) instead of
        a hard-coded About selector. Still a KEY SUFFIX, never a full section
        id — the numeric part of a section id is the TEMPLATE id and differs
        per theme, so a literal id matches nothing on the draft and then looks
        like it starts working on live.

     2. `reduce` was the ONE identifier this block borrowed from the enclosing
        IIFE in gnpab-motion.js (declared there at line 70). It is recomputed
        here from the same media query. Verified by scope analysis before the
        lift that it was the only borrowed identifier — everything else the
        block touches it declares itself.

   Behaviour is deliberately unchanged: one card every 4s, pause under the
   pointer, stop for good on first human interaction, never start under
   reduced motion, and with no JS at all Dawn's slider is left exactly as it is.
   =========================================================================== */
(function () {
  'use strict';

  var GNPAB_PRODROW_SCOPE = window.GNPAB_PRODROW_SCOPE || '[id$="__featured_collection_4KXfYz"]';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ======================================================================
     ROUND 3 — AUTO-ADVANCE ON THE ABOUT PAGE'S PRODUCT ROW. ADDENDUM 5.

     This drives the STOCK DAWN featured-collection slider from outside. Nothing
     in Dawn's section or slider files is edited, and the scroll goes through the
     same `ul.slider` Dawn listens to, so its 1/2 counter and its arrow
     disabled-states stay correct without us touching either.

     IT IS ONE CARD EVERY 4 SECONDS, NOT A CRAWL. These are buy links: a shopper
     reading a product title while it slides out from under the pointer is worse
     than no motion at all. So it steps, it pauses under the pointer, and the
     FIRST time a person touches it, it stops for good on that page view.

     THE SECTION IS FOUND BY KEY SUFFIX, NOT BY THE FULL ID. The id carries the
     TEMPLATE id, which differs per theme: live renders
     template--17094035472467__featured_collection_4KXfYz and the draft renders
     template--20769429389395__..., so an exact-id lookup finds nothing on the
     theme this is gated on. See the matching note in the section stylesheet.
     ====================================================================== */
  var gnpabRow = document.querySelector(GNPAB_PRODROW_SCOPE);

  /* ADDENDUM 7 — THE GRID BEHIND THE PRODUCT ROW IS THE EXISTING CLASS, NOT A
     COPY OF IT. Design offered either a scoped duplicate of the .gnpab-gridfield
     rule or one classList.add; this is the second, because the whole point of
     the ask is that the row carries THE SAME grid as the gnpab sections. A
     duplicated gradient is two places to change and one of them gets missed —
     the same argument as moving the tokens to :root and as --gnpab-ring.

     It costs the grid on a no-JS load. That is the honest trade and it degrades
     the right way: the full-bleed navy is CSS and lands regardless, so no-JS
     loses a texture, not a background. Nothing here adds or changes a word.

     Kept OUT of the slider block below on purpose — that block returns early
     when there is no slider or fewer than two cards, and the background must
     not depend on how many products the section happens to be showing. */
  if (gnpabRow) {
    var gnpabWrap = gnpabRow.querySelector('.color-inverse');
    if (gnpabWrap) gnpabWrap.classList.add('gnpab-gridfield');
  }

  if (gnpabRow) (function () {
    var ul = gnpabRow.querySelector('ul.slider');
    if (!ul) return;
    var cards = Array.prototype.slice.call(ul.querySelectorAll('li.grid__item'));
    if (cards.length < 2) return;

    // a hook for anything that wants to find this row without repeating the key
    gnpabRow.classList.add('gnpab-prow');

    var STEP_MS = 4000;
    var timer = 0;
    var paused = false;
    var stopped = false;          // first user interaction ends it for this page view
    var ourScrollUntil = 0;       // window during which a scroll event is ours, not theirs

    /* MEASURED AT RUN TIME, NEVER ASSUMED. Card width changes with the breakpoint,
       with Dawn's peek, and with the number of columns; a constant here would be
       right at one width and wrong at every other. */
    function stride() {
      var a = cards[0].getBoundingClientRect();
      var b = cards[1].getBoundingClientRect();
      var d = Math.round(b.left - a.left);
      return d > 0 ? d : Math.round(a.width);
    }

    function atEnd() {
      return ul.scrollLeft >= ul.scrollWidth - ul.clientWidth - 2;
    }

    function advance() {
      if (stopped || paused) return;
      if (document.visibilityState === 'hidden') return;
      /* NOTHING TO ADVANCE THROUGH IS NOT THE SAME AS BEING AT THE END, and
         conflating them produces a twitch. Measured on the draft at 1280: four
         products in three columns leaves 202px of scroll room against a 349px
         card stride. Every step is therefore already "the end", so the literal
         spec — step, clamp, wrap — gives 0 -> 202 -> 0 -> 202 for ever: a
         two-thirds-of-a-card lurch and a full snap back every eight seconds,
         while Dawn's own counter correctly never leaves page 1 because the row
         never reaches page 2.

         So the row only auto-advances when there is at least one whole card of
         travel. At 390 there is 1064px against a 358px stride and it steps
         0 / 358 / 716 / 1064 / 0 exactly as asked. The check is per tick, not
         per page load, so a resize across the breakpoint starts and stops it
         without any re-arming to get wrong.

         THIS IS A DEVIATION FROM THE ADDENDUM AND IT IS FLAGGED IN THE INBOX.
         Design may want the lurch, or may want a different desktop column count;
         both are their call, and one line here reverses it. */
      var room = ul.scrollWidth - ul.clientWidth;
      if (room < stride()) return;
      ourScrollUntil = Date.now() + 1500;
      if (atEnd()) ul.scrollTo({ left: 0, behavior: 'smooth' });
      else ul.scrollBy({ left: stride(), behavior: 'smooth' });
    }

    function start() {
      if (stopped || timer || reduce) return;
      timer = setInterval(advance, STEP_MS);
    }
    function halt() { if (timer) { clearInterval(timer); timer = 0; } }

    /* STOP MEANS STOP. Not pause -- a person who has taken the wheel keeps it for
       the rest of the page view, because the alternative is a row that starts
       moving again under someone who is reading it. */
    function stop() {
      if (stopped) return;
      stopped = true;
      halt();
    }

    /* WHEEL, TOUCHSTART AND POINTERDOWN ARE UNAMBIGUOUS -- a browser never
       synthesises them for a programmatic scroll, so they need no flag and cannot
       misfire. The `scroll` listener is the backstop the addendum asks for, and it
       IS ambiguous: our own smooth scroll emits a tail of scroll events. It is
       guarded by a timestamp window rather than a boolean, because a boolean
       cleared too early turns our own scroll into a permanent stop -- a silent
       end to the feature with no error and nothing to see. */
    ['wheel', 'touchstart', 'pointerdown'].forEach(function (ev) {
      ul.addEventListener(ev, stop, { passive: true });
    });
    ul.addEventListener('scroll', function () {
      if (Date.now() < ourScrollUntil) return;
      stop();
    }, { passive: true });
    Array.prototype.forEach.call(gnpabRow.querySelectorAll('.slider-button'), function (b) {
      b.addEventListener('click', stop);
    });

    var comp = gnpabRow.querySelector('slider-component') || ul;
    comp.addEventListener('mouseenter', function () { paused = true; });
    comp.addEventListener('mouseleave', function () { paused = false; });
    comp.addEventListener('focusin', function () { paused = true; });
    comp.addEventListener('focusout', function () { paused = false; });

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') halt();
      else start();
    });

    // reduced motion never starts it; no JS at all leaves Dawn's slider as it is
    start();
  })();
})();
